import React, {useState} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import EventNoteIcon from "@mui/icons-material/EventNote";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {useInstructorTrainings} from "../firebase/useInstructorTrainings";
import {UnauthorizedAccess} from "../app/UnauthorizedAccess";
import {
    TRAINING_FORMATS,
    TRAINING_STATUSES,
    TRAINING_STATUS_CONFIG,
    validateInstructorTraining,
} from "../calendar/trainingValidation";

export const AdminInstructorTraining = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();

    const {trainings, saveTraining, deleteTraining} = useInstructorTrainings();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState({});

    // Form state
    const [formData, setFormData] = useState({
        title: "",
        subtitle: "",
        format: "digital",
        status: "open",
        instructorName: "",
        location: "",
        scheduleSummary: "",
        registrationDeadline: "",
        registrationEmail: "",
        infoUrl: "",
        price: "",
        order: 1,
        sessions: [
            { sessionNumber: 1, dates: "", notes: "" },
        ],
    });

    const openCreateDialog = () => {
        setEditingItem(null);
        setFormData({
            title: "Kurslederopplæring",
            subtitle: "",
            format: "digital",
            status: "open",
            instructorName: "Psykologspesialist Serina Vorland",
            location: "Digitalt via Zoom",
            scheduleSummary: "kl. 09.00 til 16.00 hver dag",
            registrationDeadline: "",
            registrationEmail: "post@bekymringsmestring.no",
            infoUrl: "/get-educated",
            price: "",
            order: (trainings?.length || 0) + 1,
            sessions: [
                { sessionNumber: 1, dates: "", notes: "" },
            ],
        });
        setFormErrors({});
        setIsDialogOpen(true);
    };

    const openEditDialog = (item) => {
        setEditingItem(item);
        setFormData({
            id: item.id,
            title: item.title || "",
            subtitle: item.subtitle || "",
            format: item.format || "digital",
            status: item.status || "open",
            instructorName: item.instructorName || "",
            location: item.location || "",
            scheduleSummary: item.scheduleSummary || "",
            registrationDeadline: item.registrationDeadline || "",
            registrationEmail: item.registrationEmail || "",
            infoUrl: item.infoUrl || "",
            price: item.price || "",
            order: item.order || 1,
            sessions: Array.isArray(item.sessions) && item.sessions.length > 0
                ? item.sessions
                : [{ sessionNumber: 1, dates: "", notes: "" }],
        });
        setFormErrors({});
        setIsDialogOpen(true);
    };

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (formErrors[field]) {
            setFormErrors((prev) => ({ ...prev, [field]: null }));
        }
    };

    const handleAddSession = () => {
        setFormData((prev) => ({
            ...prev,
            sessions: [
                ...prev.sessions,
                { sessionNumber: prev.sessions.length + 1, dates: "", notes: "" },
            ],
        }));
    };

    const handleRemoveSession = (index) => {
        setFormData((prev) => {
            const next = prev.sessions.filter((_, i) => i !== index);
            return {
                ...prev,
                sessions: next.map((s, idx) => ({ ...s, sessionNumber: idx + 1 })),
            };
        });
    };

    const handleSessionChange = (index, field, value) => {
        setFormData((prev) => {
            const next = [...prev.sessions];
            next[index] = { ...next[index], [field]: value };
            return { ...prev, sessions: next };
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        const errors = validateInstructorTraining(formData);
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);
        try {
            await saveTraining(formData);
            setIsDialogOpen(false);
            setEditingItem(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmItem) return;
        setIsSubmitting(true);
        try {
            await deleteTraining(deleteConfirmItem.id);
            setDeleteConfirmItem(null);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isAdmin) {
        return <UnauthorizedAccess />;
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
            <Helmet>
                <title>Administrer kurslederopplæring | Bekymringsmestring</title>
            </Helmet>

            {/* Top Navigation & Back Action */}
            <Box sx={{ mb: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/me")}
                    variant="text"
                    color="primary"
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: 2,
                        minHeight: 44,
                        "&:focus-visible": {
                            outline: "2px solid #285238",
                            outlineOffset: "2px",
                        },
                    }}
                >
                    Tilbake til Min side
                </Button>
            </Box>

            {/* Page Header */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center" },
                    gap: 2,
                    mb: 4,
                }}
            >
                <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                        <SchoolIcon color="primary" sx={{ fontSize: 32 }} />
                        <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                            Kurslederopplæring
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary">
                        Administrer sertifiseringskurs og opplæringskull for fagpersoner.
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={openCreateDialog}
                    sx={{
                        minHeight: 44,
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 2,
                        px: 3,
                        boxShadow: "0 2px 8px rgba(40, 82, 56, 0.25)",
                    }}
                >
                    Ny opplæring
                </Button>
            </Box>

            {/* Table of Cohorts */}
            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(40, 82, 56, 0.12)",
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
                    overflowX: "auto",
                }}
            >
                <Table aria-label="Tabell over kurslederopplæring">
                    <TableHead sx={{ bgcolor: "rgba(40, 82, 56, 0.05)" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Tittel / Kull</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Format</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Kursholder</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Samlinger</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Frist</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Handlinger</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trainings && trainings.length > 0 ? (
                            trainings.map((item) => {
                                const statusConfig = TRAINING_STATUS_CONFIG[item.status] || {
                                    label: item.status,
                                    color: "default",
                                };
                                return (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            {item.title}
                                            {item.subtitle && (
                                                <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                                                    {item.subtitle}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={item.format === "digital" ? "Digital" : item.format === "fysisk" ? "Fysisk" : "Hybrid"}
                                                variant="outlined"
                                                sx={{ textTransform: "capitalize", fontWeight: 600 }}
                                            />
                                        </TableCell>
                                        <TableCell>{item.instructorName}</TableCell>
                                        <TableCell>
                                            {Array.isArray(item.sessions) ? `${item.sessions.length} samlinger` : "–"}
                                        </TableCell>
                                        <TableCell>
                                            {item.registrationDeadline || "Løpende"}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={statusConfig.label}
                                                color={statusConfig.color}
                                                sx={{ fontWeight: 700 }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Rediger">
                                                <IconButton
                                                    color="primary"
                                                    aria-label={`Rediger ${item.title}`}
                                                    onClick={() => openEditDialog(item)}
                                                    sx={{
                                                        minWidth: 44,
                                                        minHeight: 44,
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: "2px",
                                                        },
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Slett">
                                                <IconButton
                                                    color="error"
                                                    aria-label={`Slett ${item.title}`}
                                                    onClick={() => setDeleteConfirmItem(item)}
                                                    sx={{
                                                        minWidth: 44,
                                                        minHeight: 44,
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: "2px",
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary" }}>
                                    Ingen kurslederopplæringer registrert ennå. Klikk «Ny opplæring» for å opprette.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create / Edit Dialog */}
            <Dialog
                open={isDialogOpen}
                onClose={() => !isSubmitting && setIsDialogOpen(false)}
                maxWidth="md"
                fullWidth
                aria-labelledby="training-dialog-title"
            >
                <DialogTitle id="training-dialog-title" sx={{ fontWeight: 800 }}>
                    {editingItem ? "Rediger kurslederopplæring" : "Opprett ny kurslederopplæring"}
                </DialogTitle>
                <DialogContent dividers>
                    <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
                        <TextField
                            label="Tittel"
                            fullWidth
                            required
                            value={formData.title}
                            onChange={(e) => handleFieldChange("title", e.target.value)}
                            error={Boolean(formErrors.title)}
                            helperText={formErrors.title || "F.eks. Kurslederopplæring høsten 2026"}
                        />

                        <TextField
                            label="Undertittel"
                            fullWidth
                            value={formData.subtitle}
                            onChange={(e) => handleFieldChange("subtitle", e.target.value)}
                            helperText="F.eks. Digital kurslederopplæring med oppstart i september 2026"
                        />

                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField
                                select
                                label="Format"
                                value={formData.format}
                                onChange={(e) => handleFieldChange("format", e.target.value)}
                                error={Boolean(formErrors.format)}
                            >
                                <MenuItem value={TRAINING_FORMATS.DIGITAL}>Digital (Zoom / video)</MenuItem>
                                <MenuItem value={TRAINING_FORMATS.FYSISK}>Fysisk samling</MenuItem>
                                <MenuItem value={TRAINING_FORMATS.HYBRID}>Hybrid</MenuItem>
                            </TextField>

                            <TextField
                                select
                                label="Status for påmelding"
                                value={formData.status}
                                onChange={(e) => handleFieldChange("status", e.target.value)}
                                error={Boolean(formErrors.status)}
                            >
                                <MenuItem value={TRAINING_STATUSES.OPEN}>Ledige plasser</MenuItem>
                                <MenuItem value={TRAINING_STATUSES.FEW_SEATS}>Få plasser igjen</MenuItem>
                                <MenuItem value={TRAINING_STATUSES.FULL}>Fulltegnet</MenuItem>
                                <MenuItem value={TRAINING_STATUSES.COMPLETED}>Gjennomført</MenuItem>
                            </TextField>
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField
                                label="Ansvarlig kursholder"
                                fullWidth
                                required
                                value={formData.instructorName}
                                onChange={(e) => handleFieldChange("instructorName", e.target.value)}
                                error={Boolean(formErrors.instructorName)}
                                helperText={formErrors.instructorName || "F.eks. Psykologspesialist Serina Vorland"}
                            />

                            <TextField
                                label="Sted / Plattform"
                                fullWidth
                                value={formData.location}
                                onChange={(e) => handleFieldChange("location", e.target.value)}
                                helperText="F.eks. Zoom eller Adresse"
                            />
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField
                                label="Undervisningstid"
                                fullWidth
                                value={formData.scheduleSummary}
                                onChange={(e) => handleFieldChange("scheduleSummary", e.target.value)}
                                helperText="F.eks. kl. 09.00 til 16.00 hver dag"
                            />

                            <TextField
                                label="Påmeldingsfrist (ÅÅÅÅ-MM-DD)"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                value={formData.registrationDeadline}
                                onChange={(e) => handleFieldChange("registrationDeadline", e.target.value)}
                                error={Boolean(formErrors.registrationDeadline)}
                                helperText={formErrors.registrationDeadline || "Valgfri frist"}
                            />
                        </Box>

                        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                            <TextField
                                label="Påmeldings-e-post"
                                fullWidth
                                value={formData.registrationEmail}
                                onChange={(e) => handleFieldChange("registrationEmail", e.target.value)}
                                error={Boolean(formErrors.registrationEmail)}
                                helperText={formErrors.registrationEmail || "F.eks. post@bekymringsmestring.no"}
                            />

                            <TextField
                                label="Mer info lenke"
                                fullWidth
                                value={formData.infoUrl}
                                onChange={(e) => handleFieldChange("infoUrl", e.target.value)}
                                helperText="F.eks. /get-educated"
                            />
                        </Box>

                        <TextField
                            label="Kursavgift / Prisinformasjon"
                            fullWidth
                            value={formData.price}
                            onChange={(e) => handleFieldChange("price", e.target.value)}
                            helperText="F.eks. Kr 14 500,- inkl. kursmateriell"
                        />

                        {/* Multi-Session Manager */}
                        <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                                    <EventNoteIcon color="primary" /> Samlinger / Dagsamlinger ({formData.sessions.length})
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddSession}
                                    sx={{
                                        textTransform: "none",
                                        fontWeight: 600,
                                        minHeight: 44,
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: "2px",
                                        },
                                    }}
                                >
                                    Legg til samling
                                </Button>
                            </Box>

                            <Stack spacing={1.5}>
                                {formData.sessions.map((session, index) => (
                                    <Card key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 90, pt: 1 }}>
                                                {session.sessionNumber}. samling:
                                            </Typography>
                                            <TextField
                                                size="small"
                                                label="Datoer"
                                                placeholder="F.eks. 7. - 9. september"
                                                value={session.dates}
                                                onChange={(e) => handleSessionChange(index, "dates", e.target.value)}
                                                sx={{ flex: 1 }}
                                            />
                                            <TextField
                                                size="small"
                                                label="Notat / Tema"
                                                placeholder="F.eks. Digital på Zoom"
                                                value={session.notes || ""}
                                                onChange={(e) => handleSessionChange(index, "notes", e.target.value)}
                                                sx={{ flex: 1.5 }}
                                            />
                                            {formData.sessions.length > 1 && (
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    aria-label={`Fjern samling ${session.sessionNumber}`}
                                                    onClick={() => handleRemoveSession(index)}
                                                    sx={{
                                                        minWidth: 44,
                                                        minHeight: 44,
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: "2px",
                                                        },
                                                    }}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setIsDialogOpen(false)}
                        disabled={isSubmitting}
                        sx={{
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={isSubmitting}
                        sx={{
                            minHeight: 44,
                            fontWeight: 700,
                            px: 3,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {isSubmitting ? "Lagrer..." : editingItem ? "Oppdater" : "Opprett"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={Boolean(deleteConfirmItem)}
                onClose={() => !isSubmitting && setDeleteConfirmItem(null)}
                aria-labelledby="delete-confirm-title"
            >
                <DialogTitle id="delete-confirm-title" sx={{ fontWeight: 700 }}>
                    Slett kurslederopplæring?
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Er du sikker på at du vil slette «{deleteConfirmItem?.title}»? Handlingen kan ikke angres.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setDeleteConfirmItem(null)}
                        disabled={isSubmitting}
                        sx={{
                            minHeight: 44,
                            fontWeight: 600,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        Avbryt
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDelete}
                        disabled={isSubmitting}
                        sx={{
                            minHeight: 44,
                            fontWeight: 700,
                            "&:focus-visible": {
                                outline: "2px solid #285238",
                                outlineOffset: "2px",
                            },
                        }}
                    >
                        {isSubmitting ? "Sletter..." : "Slett"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};
